import { createContext, type FC, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useThreadsQuery } from "../components/StaffCommunication/util/hooks";
import { trackEvent } from "../utils/analytics";
import type { RecommendationOpenSource } from "../utils/models";
import { useHospitalData } from "./HospitalDataProvider";

type RecommendationsDrawerProps = {
	isOpen: boolean;
	toggleDrawerOpen: (open: boolean, source: RecommendationOpenSource) => void;
};

const RecommendationsDrawerContext = createContext<RecommendationsDrawerProps | null>(null);

export const RecommendationsDrawerProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const [isOpen, setIsOpen] = useState<boolean>(false);

	const toggleDrawerOpen = useCallback((open: boolean, source: RecommendationOpenSource) => {
		setIsOpen(open);
		open ? trackEvent("recommendations_drawer_opened", { source }) : trackEvent("recommendations_drawer_closed", { source });
	}, []);

	return <RecommendationsDrawerContext.Provider value={{ isOpen, toggleDrawerOpen }}>{children}</RecommendationsDrawerContext.Provider>;
};

export const useRecommendationsDrawerContext = (): RecommendationsDrawerProps => {
	const context = useContext(RecommendationsDrawerContext);

	if (!context) {
		throw Error("`useRecommendationsDrawerContext` must be used within a `RecommendationsDrawerContext`");
	}

	return context;
};

export type DrawerTriggerNotification = {
	tooltipText: string;
	color: "error" | "success" /*THIS is NOT to indicate an error state, but rather color it as green or red. */;
} | null;

/**
 * Sibling to `useRecommendationsDrawerContext` — deliberately NOT folded into it. Only the 4 call
 * sites that render the CopilotButton-opening trigger need the notification computation (which polls
 * `useThreadsQuery` every 2 minutes); the other `useRecommendationsDrawerContext` call sites only want
 * `isOpen`/`toggleDrawerOpen` and would otherwise pick up redundant polling subscribers for free.
 */
export const useDrawerTriggerNotification = () => {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const { data } = useThreadsQuery(chosenDepartment.department_id);
	const unreadMessagesCount = useMemo(() => Object.values(data?.unreadCounts ?? {}).reduce((sum, count) => sum + count, 0), [data]);

	const getDrawerTriggerNotification = useCallback(
		(requestsWithResponsesCount: number): DrawerTriggerNotification => {
			if (unreadMessagesCount === 0 && requestsWithResponsesCount === 0) return null;
			const parts: string[] = [];
			if (unreadMessagesCount > 0) parts.push(t("drawerTrigger.newMessages", { count: unreadMessagesCount }));
			if (requestsWithResponsesCount > 0) parts.push(t("drawerTrigger.requestsWithResponses", { count: requestsWithResponsesCount }));
			// Requests take priority when both are present — a pending pickup response needs action,
			// an unread message is just informational.
			return { tooltipText: parts.join(" · "), color: requestsWithResponsesCount > 0 ? "error" : "success" };
		},
		[unreadMessagesCount, t],
	);

	return { getDrawerTriggerNotification };
};
